import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useToast } from "@/hooks/use-toast";
import { useMobile } from "@/hooks/useMobile";
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Send, 
  Download,
  Calendar,
  Users,
  ExternalLink,
  Wrench,
  RefreshCw,
  RotateCcw
} from "lucide-react";
import { format } from "date-fns";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SignatureTrackingCardProps {
  signatureRequestId: string;
  debugMode?: boolean;
}

export default function SignatureTrackingCard({ signatureRequestId, debugMode = false }: SignatureTrackingCardProps) {
  const [request, setRequest] = useState<any>(null);
  const [signers, setSigners] = useState<any[]>([]);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resending, setResending] = useState(false);
  const [allDocumentRequests, setAllDocumentRequests] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const { toast: showToast } = useToast();
  const isMobile = useMobile();

  useEffect(() => {
    loadData();
    
    // Set up real-time subscription with notifications
    const channel = supabase
      .channel(`signature-request-${signatureRequestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'document_signers',
          filter: `signature_request_id=eq.${signatureRequestId}`,
        },
        (payload) => {
          const newStatus = payload.new.status;
          const signerName = payload.new.signer_name;
          
          if (newStatus === 'signed') {
            loadData().then(async () => {
              const { data: freshSigners } = await supabase
                .from('document_signers')
                .select('*')
                .eq('signature_request_id', request?.id || signatureRequestId);
              
              const signedCount = freshSigners?.filter(s => s.status === 'signed').length || 0;
              const totalCount = freshSigners?.length || 0;
              
              if (signedCount === totalCount) {
                toast.success("🎉 All signatures collected!", {
                  description: "Document is now complete and ready for governance archiving.",
                });
              } else {
                toast.success(`${signerName} signed (${signedCount} of ${totalCount})`, {
                  description: "Signature received successfully.",
                });
              }
            });
          }
          
          loadData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [signatureRequestId, request?.status]);

  const loadData = async () => {
    try {
      const { data: initialRequest } = await supabase
        .from('signature_requests')
        .select(`
          *,
          document:generated_documents!signature_requests_document_id_fkey(document_name, file_url, signed_file_url)
        `)
        .eq('id', signatureRequestId)
        .maybeSingle();

      if (!initialRequest) {
        console.log('❌ No signature request found');
        return;
      }

      // Get ALL signature requests for this task to find the best one
      const { data: allRequests } = await supabase
        .from('signature_requests')
        .select('*')
        .eq('task_id', initialRequest.task_id)
        .order('created_at', { ascending: false });

      // Pick the BEST request (completed > pending, more signatures > fewer)
      let bestRequest = initialRequest;
      let bestRequestId = signatureRequestId;

      for (const req of allRequests || []) {
        const { data: reqSigners } = await supabase
          .from('document_signers')
          .select('status')
          .eq('signature_request_id', req.id);

        const currentSignedCount = reqSigners?.filter((s: any) => s.status === 'signed').length || 0;
        
        const { data: bestSigners } = await supabase
          .from('document_signers')
          .select('status')
          .eq('signature_request_id', bestRequestId);
        
        const bestSignedCount = bestSigners?.filter((s: any) => s.status === 'signed').length || 0;

        if (req.status === 'completed' && bestRequest.status !== 'completed') {
          bestRequest = { ...initialRequest, ...req };
          bestRequestId = req.id;
          continue;
        }

        if (req.status === bestRequest.status && currentSignedCount > bestSignedCount) {
          bestRequest = { ...initialRequest, ...req };
          bestRequestId = req.id;
          continue;
        }
      }

      // Load signers for the BEST request
      const { data: signersData } = await supabase
        .from('document_signers')
        .select('*')
        .eq('signature_request_id', bestRequestId)
        .order('signing_order');

      setRequest(bestRequest);
      setSigners(signersData || []);

      // Load audit log
      const { data: auditData } = await supabase
        .from('signature_audit_log')
        .select('*')
        .eq('signature_request_id', bestRequestId)
        .order('created_at', { ascending: false })
        .limit(20);

      setAuditLog(auditData || []);

      // Load generated document details
      if (bestRequest?.document_id) {
        const { data: docData } = await supabase
          .from('generated_documents')
          .select('*')
          .eq('id', bestRequest.document_id)
          .single();
        
        setDocument(docData);
      }

      // Load all signature requests for this document to show history
      if (bestRequest?.document_id) {
        await loadAllDocumentRequests(bestRequest.document_id);
      }
    } catch (error) {
      console.error('Error loading signature data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllDocumentRequests = async (documentId: string) => {
    try {
      const { data, error } = await supabase
        .from('signature_requests')
        .select(`
          *,
          document_signers (
            id,
            status,
            signer_name,
            signed_at
          )
        `)
        .eq('document_id', documentId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAllDocumentRequests(data || []);
    } catch (error) {
      console.error('Error loading document request history:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
      toast.success('Status refreshed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to refresh status');
    } finally {
      setRefreshing(false);
    }
  };

  const sendReminder = async (signerId: string, signerName: string) => {
    try {
      const { error } = await supabase.functions.invoke('send-signature-reminder', {
        body: { signatureRequestId, signerId }
      });

      if (error) throw error;

      toast.success(`Reminder sent to ${signerName}`);
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reminder');
    }
  };

  const handleResendRequest = async () => {
    setResending(true);
    try {
      const documentId = request.document_id;
      const taskId = request.task_id;
      const unaId = request.una_id;

      // Create new signature request
      const { data: newRequest, error: newRequestError } = await supabase
        .from('signature_requests')
        .insert({
          document_id: documentId,
          task_id: taskId,
          una_id: unaId,
          status: 'pending',
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          created_by: request.created_by,
        })
        .select()
        .single();

      if (newRequestError) throw newRequestError;

      // Copy ONLY pending/viewed signers
      const pendingSigners = signers.filter(signer => 
        signer.status !== 'signed' && signer.status !== 'completed'
      );
      
      pendingSigners.sort((a, b) => a.signing_order - b.signing_order);
      
      if (pendingSigners.length === 0) {
        throw new Error('All signers have already completed signing');
      }
      
      const signersToCreate = pendingSigners.map((signer, index) => ({
        signature_request_id: newRequest.id,
        signer_name: signer.signer_name,
        signer_email: signer.signer_email,
        signer_role: signer.signer_role,
        signing_order: index + 1,
        status: 'pending',
      }));

      const { error: signersError } = await supabase
        .from('document_signers')
        .insert(signersToCreate);

      if (signersError) throw signersError;

      // Send emails to pending signers
      for (const signer of pendingSigners) {
        try {
          await supabase.functions.invoke('send-member-signature-request', {
            body: {
              signatureRequestId: newRequest.id,
              signerEmail: signer.signer_email,
              signerName: signer.signer_name,
            }
          });
        } catch (emailError) {
          console.error('Error sending signature email:', emailError);
        }
      }

      // Log audit event
      await supabase.from('signature_audit_log').insert({
        signature_request_id: newRequest.id,
        event_type: 'signature_request_resent',
        event_data: {
          original_request_id: signatureRequestId,
          signers_count: pendingSigners.length,
        },
      });

      toast.success(`Signature request re-sent to ${pendingSigners.length} signer(s)`);
      setShowResendDialog(false);
      await loadData();
      
    } catch (error: any) {
      console.error('Error re-sending signature request:', error);
      toast.error(error.message || 'Failed to re-send signature request');
    } finally {
      setResending(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: any }> = {
      pending: { variant: 'secondary', icon: Clock },
      viewed: { variant: 'default', icon: Eye },
      signed: { variant: 'default', icon: CheckCircle },
      declined: { variant: 'destructive', icon: XCircle },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getOverallStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: 'secondary',
      in_progress: 'default',
      completed: 'default',
      declined: 'destructive',
      expired: 'destructive',
      cancelled: 'secondary',
    };

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return <Card className="p-6">Loading...</Card>;
  }

  if (!request) {
    return <Card className="p-6">Signature request not found</Card>;
  }

  const signedCount = signers.filter(s => s.status === 'signed').length;
  const totalCount = signers.length;
  const progress = (signedCount / totalCount) * 100;

  return (
    <Card className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold">{request.document?.document_name}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Created {format(new Date(request.created_at), 'PPP')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Status
          </Button>
          
          {request.status !== 'completed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResendDialog(true)}
              disabled={resending}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Re-send Request
            </Button>
          )}
          {getOverallStatusBadge(request.status)}
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {signedCount} of {totalCount} signed
          </span>
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Expires {format(new Date(request.expires_at), 'PPP')}
          </span>
        </div>
        <Progress value={progress} />
      </div>

      {/* Signers - Mobile Card View or Desktop Table */}
      {isMobile ? (
        <div className="space-y-3">
          {signers.map((signer) => (
            <Card key={signer.id} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-medium">{signer.signer_name}</div>
                  <div className="text-sm text-muted-foreground">{signer.signer_email}</div>
                </div>
                {getStatusBadge(signer.status)}
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                <p>Role: {signer.signer_role}</p>
                <p>Signed: {signer.signed_at ? format(new Date(signer.signed_at), 'PPp') : '-'}</p>
              </div>
              <div className="flex gap-2">
                {signer.signing_url && (signer.status === 'pending' || signer.status === 'viewed') && (
                  <Button size="sm" variant="default" className="min-h-[44px]" asChild>
                    <a href={signer.signing_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Sign
                    </a>
                  </Button>
                )}
                {(signer.status === 'pending' || signer.status === 'viewed') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-h-[44px]"
                    onClick={() => sendReminder(signer.id, signer.signer_name)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Remind
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-3 text-sm font-medium">Signer</th>
                <th className="text-left p-3 text-sm font-medium">Role</th>
                <th className="text-left p-3 text-sm font-medium">Status</th>
                <th className="text-left p-3 text-sm font-medium">Signed At</th>
                <th className="text-right p-3 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signers.map((signer) => (
                <tr key={signer.id} className="border-t">
                  <td className="p-3">
                    <div>
                      <div className="font-medium">{signer.signer_name}</div>
                      <div className="text-sm text-muted-foreground">{signer.signer_email}</div>
                    </div>
                  </td>
                  <td className="p-3 text-sm">{signer.signer_role}</td>
                  <td className="p-3">{getStatusBadge(signer.status)}</td>
                  <td className="p-3 text-sm">
                    {signer.signed_at 
                      ? format(new Date(signer.signed_at), 'PPp')
                      : '-'
                    }
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      {signer.signing_url && (signer.status === 'pending' || signer.status === 'viewed') && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="default"
                                asChild
                              >
                                <a href={signer.signing_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Sign
                                </a>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Opens signing page
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {(signer.status === 'pending' || signer.status === 'viewed') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendReminder(signer.id, signer.signer_name)}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Remind
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Timeline */}
      <div className="space-y-2">
        <h4 className="font-medium">Activity Timeline</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {auditLog.map((event) => (
            <div key={event.id} className="flex items-start gap-3 text-sm p-2 rounded-lg bg-muted">
              <div className="flex-1">
                <div className="font-medium">{event.event_type.replace(/_/g, ' ')}</div>
                <div className="text-muted-foreground">
                  {format(new Date(event.created_at), 'PPp')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Signed Document Download */}
      {request.status === 'completed' && (() => {
        const signedUrl = (request.document as any)?.signed_file_url;
        
        if (!signedUrl) return null;
        
        const documentName = request.document?.document_name;
        const filename = `${documentName?.toString().replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'document'}-signed.pdf`;
        const signedDownloadUrl = `${signedUrl}?download=${encodeURIComponent(filename)}`;
        
        const isPreview = window.location.hostname.endsWith('lovableproject.com');

        if (isPreview) {
          return (
            <Button 
              variant="outline" 
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              asChild
            >
              <a href={signedDownloadUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Download Signed Document
              </a>
            </Button>
          );
        }

        return (
          <Button 
            variant="outline" 
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            onClick={async () => {
              try {
                const res = await fetch(signedUrl, { cache: 'no-store' });
                if (!res.ok) throw new Error('Failed to fetch signed PDF');
                const blob = await res.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
                
                showToast({ title: 'Download Started', description: 'Signed document is downloading...' });
              } catch (error) {
                console.error('Download error:', error);
                showToast({ title: 'Download Failed', description: 'Could not download signed document', variant: 'destructive' });
              }
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Download Signed Document
          </Button>
        );
      })()}

      {/* Document History Section */}
      {allDocumentRequests.length > 1 && (
        <Collapsible open={showHistory} onOpenChange={setShowHistory}>
          <div className="border-t pt-4">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Document History ({allDocumentRequests.length} requests)
                </span>
                <span className="text-xs text-muted-foreground">
                  {showHistory ? 'Hide' : 'Show'} previous versions
                </span>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3 space-y-2">
              {allDocumentRequests.map((req) => {
                const reqSignedCount = req.document_signers.filter((s: any) => s.status === 'signed').length;
                const reqTotalCount = req.document_signers.length;
                const isCurrent = req.id === signatureRequestId;

                return (
                  <div
                    key={req.id}
                    className={`p-3 rounded-lg border ${
                      isCurrent ? 'bg-primary/5 border-primary' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {isCurrent && (
                            <Badge variant="outline" className="text-xs">Current</Badge>
                          )}
                          {getOverallStatusBadge(req.status)}
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(req.created_at), 'PPp')}
                          </span>
                        </div>
                        <div className="text-sm">
                          {reqSignedCount} of {reqTotalCount} signed
                          {req.status === 'completed' && req.completed_at && (
                            <span className="text-muted-foreground ml-2">
                              · Completed {format(new Date(req.completed_at), 'PPp')}
                            </span>
                          )}
                        </div>
                      </div>
                      {!isCurrent && req.status === 'completed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.location.hash = `#signature-${req.id}`;
                            window.location.reload();
                          }}
                        >
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {/* Debug Section */}
      {debugMode && (
        <div className="space-y-4 mt-4 border-t pt-4 bg-muted/30 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            <h5 className="font-medium text-sm">Debug Information</h5>
          </div>

          {/* Signature Request Data */}
          <div className="space-y-2">
            <h5 className="font-medium text-sm">Signature Request Data</h5>
            <div className="text-xs bg-muted p-3 rounded-lg space-y-1">
              <div><span className="font-medium">ID:</span> {request.id}</div>
              <div><span className="font-medium">Status:</span> {request.status}</div>
              <div><span className="font-medium">Created:</span> {format(new Date(request.created_at), 'PPp')}</div>
              <div><span className="font-medium">Updated:</span> {format(new Date(request.updated_at), 'PPp')}</div>
              <div><span className="font-medium">Expires:</span> {format(new Date(request.expires_at), 'PPp')}</div>
            </div>
          </div>

          {/* Document Signers Data */}
          <div className="space-y-2">
            <h5 className="font-medium text-sm">Document Signers Data</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border rounded-lg">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 border-b">Name</th>
                    <th className="text-left p-2 border-b">Email</th>
                    <th className="text-left p-2 border-b">Role</th>
                    <th className="text-left p-2 border-b">Status</th>
                    <th className="text-left p-2 border-b">Signed At</th>
                    <th className="text-left p-2 border-b">Updated At</th>
                  </tr>
                </thead>
                <tbody>
                  {signers.map((signer) => (
                    <tr key={signer.id} className="border-b">
                      <td className="p-2">{signer.signer_name}</td>
                      <td className="p-2">{signer.signer_email}</td>
                      <td className="p-2">{signer.signer_role}</td>
                      <td className="p-2">{signer.status}</td>
                      <td className="p-2">{signer.signed_at ? format(new Date(signer.signed_at), 'PPp') : '-'}</td>
                      <td className="p-2">{signer.updated_at ? format(new Date(signer.updated_at), 'PPp') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Generated Document Data */}
          {document && (
            <div className="space-y-2">
              <h5 className="font-medium text-sm">Generated Document Data</h5>
              <div className="text-xs bg-muted p-3 rounded-lg space-y-1">
                <div><span className="font-medium">Document ID:</span> {document.id}</div>
                <div><span className="font-medium">Name:</span> {document.document_name}</div>
                <div><span className="font-medium">Signature Version:</span> {document.signature_version || 0}</div>
                <div><span className="font-medium">All Signatures Collected:</span> {document.all_signatures_collected ? 'Yes' : 'No'}</div>
                <div><span className="font-medium">Signature Updated At:</span> {document.signature_updated_at ? format(new Date(document.signature_updated_at), 'PPp') : 'Never'}</div>
                <div><span className="font-medium">Signed File URL:</span> {document.signed_file_url ? 'Present' : 'None'}</div>
              </div>
            </div>
          )}

          {/* Status Comparison */}
          <div className="space-y-2">
            <h5 className="font-medium text-sm">Status Comparison</h5>
            <div className="text-xs bg-muted p-3 rounded-lg">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="font-medium mb-1">Database Values:</div>
                  <ul className="space-y-1">
                    <li>Request Status: <span className="font-mono">{request.status}</span></li>
                    <li>Signatures Collected: <span className="font-mono">{document?.all_signatures_collected ? 'Yes' : 'No'}</span></li>
                    <li>Signature Version: <span className="font-mono">{document?.signature_version || 0}</span></li>
                    <li>Signed Count: <span className="font-mono">{signers.filter(s => s.status === 'signed').length}/{signers.length}</span></li>
                  </ul>
                </div>
                <div>
                  <div className="font-medium mb-1">UI Display Values:</div>
                  <ul className="space-y-1">
                    <li>Progress: <span className="font-mono">{signedCount}/{totalCount}</span></li>
                    <li>Percent: <span className="font-mono">{Math.round(progress)}%</span></li>
                    <li>Overall Status: <span className="font-mono">{request.status}</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Re-send Confirmation Dialog */}
      <AlertDialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-send Signature Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send fresh signing links to pending signers. 
              Previous signing links will continue to work until they sign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResendRequest}
              disabled={resending}
              className="bg-primary"
            >
              {resending ? 'Re-sending...' : 'Re-send Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
